import {
  Toast,
  ToastClose,
  ToastDescription,
  ToastProvider,
  ToastTitle,
  ToastViewport,
} from "./toast";
import { useToast } from "./use-toast";
import { Icon } from "@core/components/UI/Icon";
import { cn } from "@core/utils/cn";
import type { IconKey } from "@core/assets";

type Variant = "default" | "success" | "info" | "warning" | "destructive";

const VARIANT_CONFIG: Record<
  Variant,
  {
    iconName: IconKey | null;
    iconClassName: string;
  }
> = {
  default: { iconName: null, iconClassName: "" },
  success: { iconName: "circle-check-big", iconClassName: "text-accent-green-700" },
  info: {
    iconName: "circle-alert",
    iconClassName: "text-accent-blue-800 rotate-180",
  },
  warning: { iconName: "triangle-alert", iconClassName: "text-accent-yellow-500" },
  destructive: { iconName: "circle-x", iconClassName: "text-accent-red-600" },
};

export function Toaster() {
  const { toasts } = useToast();

  return (
    <ToastProvider swipeDirection="right">
      {toasts.map(({ id, title, description, action, variant, ...props }) => {
        const { iconName, iconClassName } =
          VARIANT_CONFIG[(variant as Variant) ?? "default"] ??
          VARIANT_CONFIG.default;

        return (
          <Toast key={id} variant={variant} {...props}>
            {/* Left: icon + content */}
            <div className="flex items-start gap-3 min-w-0 flex-1">
              {iconName && (
                <Icon
                  name={iconName}
                  className={cn("flex-shrink-0 mt-0.5 w-5 h-5", iconClassName)}
                />
              )}
              <div className="flex flex-col gap-2 min-w-0">
                <div className="flex flex-col gap-1">
                  {title && <ToastTitle>{title}</ToastTitle>}
                  {description && (
                    <ToastDescription>{description}</ToastDescription>
                  )}
                </div>
                {action && <div className="flex gap-2">{action}</div>}
              </div>
            </div>

            {/* Right: close */}
            <ToastClose />
          </Toast>
        );
      })}
      <ToastViewport />
    </ToastProvider>
  );
}
